import React, { useState } from 'react';
import axios from 'axios';
import { Download, Calendar } from 'lucide-react';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const AmmoAnnualReport = () => {
  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [includeCategories, setIncludeCategories] = useState(false);
  const currentYear = new Date().getFullYear();
  
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${API_BASE}/api/admin/generate-ammo-annual-report`,
        {
          start_date: startDate,
          end_date: endDate,
          include_categories: includeCategories
        },
        {
          headers: { 
            Authorization: `Bearer ${token}` 
          },
          responseType: 'blob' // Important for file download
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      const fileName = startYear === endYear 
        ? `ammo-rapport-${startYear}.pdf`
        : `ammo-rapport-${startYear}-${endYear}.pdf`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Årsoppgjør lastet ned!');
    } catch (error) {
      console.error('Error generating annual report:', error);
      const errorMsg = error.response?.data?.detail || 'Kunne ikke generere årsoppgjør';
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const setPresetPeriod = (preset) => {
    const now = new Date();
    const year = now.getFullYear();
    
    switch (preset) {
      case 'current_year':
        setStartDate(`${year}-01-01`);
        setEndDate(`${year}-12-31`);
        break;
      case 'last_year':
        setStartDate(`${year - 1}-01-01`);
        setEndDate(`${year - 1}-12-31`);
        break;
      case 'last_6_months':
        const sixMonthsAgo = new Date(now);
        sixMonthsAgo.setMonth(now.getMonth() - 6);
        setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
        setEndDate(now.toISOString().split('T')[0]);
        break;
      case 'last_3_months':
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        setEndDate(now.toISOString().split('T')[0]);
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <Calendar className="w-5 h-5 mr-2" />
        Årsoppgjør ammo
      </h3>
      
      <p className="text-sm text-gray-400 mb-6">
        Last ned en detaljert PDF-rapport med grafer og statistikk for en valgt periode.
      </p>

      {/* Quick Presets */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Hurtigvalg:
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <button
            onClick={() => setPresetPeriod('current_year')}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm text-white transition-colors"
          >
            {currentYear}
          </button>
          <button
            onClick={() => setPresetPeriod('last_year')}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm text-white transition-colors"
          >
            {currentYear - 1}
          </button>
          <button
            onClick={() => setPresetPeriod('last_6_months')}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm text-white transition-colors"
          >
            Siste 6 mnd
          </button>
          <button
            onClick={() => setPresetPeriod('last_3_months')}
            className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm text-white transition-colors"
          >
            Siste 3 mnd
          </button>
        </div>
      </div>

      {/* Date Range */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Fra dato:
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Til dato:
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mb-6">
        <div className="text-sm text-blue-200">
          <p className="font-medium mb-2">📊 Rapporten inkluderer:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-300">
            <li>Oppsummering av total omsetning og transaksjoner</li>
            <li>Detaljert tabell med salg per kaliber</li>
            <li>Sammenligning med forrige periode</li>
            <li>Gjeldende lagerstatus</li>
          </ul>
        </div>
      </div>

      {/* Include Categories Checkbox */}
      <div className="mb-6">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={includeCategories}
            onChange={(e) => setIncludeCategories(e.target.checked)}
            className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
          />
          <span className="text-sm text-gray-300">
            Vis med ammo-kategorier (interne aktiviteter og egendefinerte kategorier)
          </span>
        </label>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerateReport}
        disabled={generating || !startDate || !endDate}
        className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
      >
        <Download className="w-5 h-5" />
        <span>{generating ? 'Genererer...' : 'Last ned årsoppgjør (PDF)'}</span>
      </button>
    </div>
  );
};

export default AmmoAnnualReport;
